import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pill, ClipboardList, Calculator, AlertCircle } from "lucide-react";

/**
 * SpecialtyConsole — shared page rendered for Pulmonology, Gastroenterology,
 * Rheumatology, and Infectious Disease. Reads the specialty id from props,
 * fetches /api/specialties/{id}/reference and renders 3 tabs:
 *   - Formulary  (drugs)
 *   - Care Plans (structured protocols)
 *   - Calculators (list of available scoring endpoints for this specialty)
 * Encounter workflows are the primary UX; this page is the read-only reference.
 */
export default function SpecialtyConsole({ specialtyId, title, tagline,
                                            accent = "#38BDF8", calculators = [] }) {
  const [tab, setTab] = useState("overview");
  const [ref, setRef] = useState({ formulary: [], care_plans: [], guideline: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/specialties/${specialtyId}/reference`);
        setRef(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to load ${title} reference`);
      } finally {
        setLoading(false);
      }
    })();
  }, [specialtyId, title]);

  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading {title}…
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-6 md:px-10 py-6"
         data-testid={`specialty-${specialtyId}`}>
      {/* Header */}
      <div className="ros-card"
           style={{ borderColor: `${accent}60`, background: `${accent}0A` }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label" style={{ color: accent }}>{title}</div>
            <div className="font-heading text-2xl mt-1">{tagline}</div>
            <div className="text-xs text-[#A3ACA7] mt-1">
              {ref.guideline || "Reference module"} · {ref.formulary?.length ?? 0} drugs · {ref.care_plans?.length ?? 0} care plans · {calculators.length} calculators
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[
              { key: "overview",   label: "Overview",   icon: AlertCircle },
              { key: "formulary",  label: "Formulary",  icon: Pill },
              { key: "care_plans", label: "Care Plans", icon: ClipboardList },
              { key: "calc",       label: "Calculators", icon: Calculator },
            ].map((t) => {
              const Icon = t.icon;
              const on = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                        data-testid={`${specialtyId}-tab-${t.key}`}
                        className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold transition-colors ${
                          on ? "text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                        }`}
                        style={on ? { background: accent } : {}}>
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4"
             data-testid={`${specialtyId}-overview`}>
          <MetricCard label="Formulary drugs" value={ref.formulary?.length ?? 0}
                      hint="Curated across common regimens" accent={accent} />
          <MetricCard label="Care plans" value={ref.care_plans?.length ?? 0}
                      hint="Multi-week protocols with checkpoints" accent={accent} />
          <MetricCard label="Calculators" value={calculators.length}
                      hint="Validated scoring tools" accent={accent} />
          <div className="ros-card md:col-span-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
              Clinical guideline
            </div>
            <div className="text-sm text-white">{ref.guideline}</div>
            <div className="text-xs text-[#A3ACA7]">
              Use the encounter workspace (Check-ins) to prescribe from the formulary and
              order investigations for {title.toLowerCase()} presentations.
            </div>
          </div>
        </div>
      )}

      {tab === "formulary" && (
        <div className="ros-card overflow-x-auto" data-testid={`${specialtyId}-formulary`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#A3ACA7] text-left">
                <th className="py-2 pr-3">Drug</th><th className="py-2 pr-3">Class</th>
                <th className="py-2 pr-3">Indications</th>
                <th className="py-2 pr-3">Cautions</th>
                <th className="py-2 pr-3">Risk</th>
              </tr>
            </thead>
            <tbody>
              {ref.formulary.map((d, i) => (
                <tr key={`${d.name}-${i}`} className="border-t border-[#272E2B] align-top">
                  <td className="py-2 pr-3 text-white">{d.name}</td>
                  <td className="py-2 pr-3 text-[#A3ACA7]">{d.class}</td>
                  <td className="py-2 pr-3 text-[#A3ACA7]">
                    {(d.indications || []).join(" · ")}
                  </td>
                  <td className="py-2 pr-3 text-[#A3ACA7] text-xs">{d.cautions}</td>
                  <td className="py-2 pr-3">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{
                            color:
                              d.risk_level === "avoid" ? "#F87171"
                              : d.risk_level === "caution" ? "#FBBF24" : "#4ADE80",
                            background:
                              d.risk_level === "avoid" ? "rgba(248,113,113,0.12)"
                              : d.risk_level === "caution" ? "rgba(251,191,36,0.12)" : "rgba(74,222,128,0.12)",
                          }}>
                      {(d.risk_level || "safe").toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "care_plans" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4"
             data-testid={`${specialtyId}-care-plans`}>
          {ref.care_plans.map((p) => (
            <div key={p.id} className="ros-card space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading text-lg leading-tight">{p.title}</div>
                  {p.stage && (
                    <div className="text-[10px] text-[#7A8480] mt-0.5">{p.stage}</div>
                  )}
                </div>
                {(p.review_cadence || p.duration_weeks) && (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap"
                        style={{ background: `${accent}18`, color: accent }}>
                    {p.review_cadence || `${p.duration_weeks} weeks`}
                  </span>
                )}
              </div>

              {/* Advanced schema — patient_goals + provider_tasks */}
              {p.patient_goals && p.patient_goals.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-[#4ADE80] flex items-center gap-1">
                    <span>✓</span> Patient goals
                  </div>
                  <ul className="space-y-1">
                    {p.patient_goals.map((g, i) => (
                      <li key={i} className="text-xs text-[#A3ACA7] flex items-start gap-2">
                        <span style={{ color: "#4ADE80" }} className="mt-0.5">•</span>
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.provider_tasks && p.provider_tasks.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-[#38BDF8] flex items-center gap-1">
                    <span>✎</span> Care-team tasks
                  </div>
                  <ul className="space-y-1">
                    {p.provider_tasks.map((t, i) => (
                      <li key={i} className="text-xs text-[#A3ACA7] flex items-start gap-2">
                        <span style={{ color: "#38BDF8" }} className="mt-0.5">•</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Legacy schema fallback — weekly steps (only if no advanced fields present) */}
              {!p.patient_goals && p.steps && (
                <ol className="space-y-1.5">
                  {p.steps.map((s, i) => (
                    <li key={i} className="text-xs text-[#A3ACA7] flex items-start gap-2">
                      <span className="text-[10px] font-bold min-w-[38px]"
                            style={{ color: accent }}>W{s.week}</span>
                      <span>{s.action}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "calc" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4"
             data-testid={`${specialtyId}-calculators`}>
          {calculators.length === 0 ? (
            <div className="ros-card text-xs text-[#7A8480]">No calculators defined yet.</div>
          ) : calculators.map((c) => (
            <CalculatorCard key={c.endpoint} spec={specialtyId} accent={accent} {...c} />
          ))}
        </div>
      )}
    </div>
  );
}


function MetricCard({ label, value, hint, accent }) {
  return (
    <div className="ros-card space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</div>
      <div className="font-heading text-3xl" style={{ color: accent }}>{value}</div>
      <div className="text-xs text-[#7A8480]">{hint}</div>
    </div>
  );
}


function CalculatorCard({ spec, accent, name, endpoint, fields, defaults = {} }) {
  const [form, setForm] = useState(() => ({ ...defaults }));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function compute() {
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => {
          if (v === "" || v === undefined || v === null) return [k, v];
          // Fields explicitly marked as list-of-ints (comma-separated string)
          const fieldSpec = fields.find((f) => f.name === k);
          if (fieldSpec?.parse === "int_list") {
            return [k, String(v).split(",").map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x))];
          }
          if (fieldSpec?.parse === "string_list") {
            return [k, String(v).split(",").map((x) => x.trim()).filter(Boolean)];
          }
          if (v === "true" || v === true) return [k, true];
          if (v === "false" || v === false) return [k, false];
          const n = Number(v);
          return [k, Number.isNaN(n) ? v : n];
        })
      );
      const { data } = await api.post(`/specialties/${spec}/scores/${endpoint}`, payload);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${name} failed`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ros-card space-y-3" data-testid={`${spec}-calc-${endpoint}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading text-lg">{name}</div>
        <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
          /scores/{endpoint}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {fields.map((f) => (
          <label key={f.name} className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
              {f.label} {f.hint && <span className="text-[#7A8480]">· {f.hint}</span>}
            </span>
            {f.type === "select" ? (
              <select value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="ros-input"
                      data-testid={`${spec}-${endpoint}-${f.name}`}>
                {f.options.map((o) => (
                  <option key={String(o.value)} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input type={f.type || "number"}
                     value={form[f.name] ?? ""}
                     step={f.step || "any"}
                     onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                     className="ros-input"
                     data-testid={`${spec}-${endpoint}-${f.name}`}
                     placeholder={f.placeholder} />
            )}
          </label>
        ))}
      </div>
      <button onClick={compute} disabled={busy}
              data-testid={`${spec}-${endpoint}-compute`}
              className="text-sm px-4 py-2 rounded-full font-semibold text-black disabled:opacity-50"
              style={{ background: accent }}>
        {busy ? "Computing…" : "Compute"}
      </button>
      {result && (
        <div className="ros-card-raised space-y-1"
             data-testid={`${spec}-${endpoint}-result`}>
          <div className="font-heading text-lg" style={{ color: accent }}>
            {result.band || result.class || result.grade || "Result"}
            {result.score !== undefined && <> · <span className="text-white">{result.score}{result.max_score ? `/${result.max_score}` : ""}</span></>}
          </div>
          {result.recommendation && (
            <div className="text-xs text-[#A3ACA7]">{result.recommendation}</div>
          )}
          {result.guideline && (
            <div className="text-[10px] uppercase tracking-wider text-[#7A8480]">
              {result.guideline}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
